import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import useScreen from 'hooks/useScreen';
import MobileModal from 'components/consumer/MobileModal';
import useOnEsc from 'hooks/useOnEsc';
import useTrapFocus from 'hooks/useTrapFocus';
import useFocusOnOpen from 'hooks/useFocusOnOpen';
import withBrowserOnly from 'components/hoc/withBrowserOnly';

const PushAcrossSidebar = ({
  children,
  desktopFilterSize = 0,
  isFullHeight = false,
  isMaterialSwatch = false,
  isOpen,
  isPhotoModalOpen = false,
  mobileClassName = '',
  mobileContentUnderHeader,
  mobileHeaderRight,
  mobileHeaderRightClassName = '',
  mobileHideHeader = false,
  mobileContentNoPadding,
  mobileOnClose,
  mobileTitle,
  mobileVariant,
  stickyRef,
  variant,
}) => {
  const { isMobileOrTablet, windowWidth } = useScreen();

  const modalRef = useFocusOnOpen(isOpen);

  useOnEsc(isOpen, mobileOnClose);
  useTrapFocus(isOpen, modalRef);

  const isFabricGrid = variant === 'fabric-grid';

  if (isMobileOrTablet) {
    return (
      <MobileModal
        className={mobileClassName}
        closeIcon="arrow"
        contentNoPadding={mobileContentNoPadding}
        contentUnderHeader={mobileContentUnderHeader}
        enableHashHistory={false}
        headerCenter={mobileTitle}
        headerRight={mobileHeaderRight}
        headerRightClassName={mobileHeaderRightClassName}
        hideHeader={mobileHideHeader}
        isFullHeight
        isMaterialSwatch={isMaterialSwatch}
        isOpen={isOpen}
        onClose={mobileOnClose}
        variant={mobileVariant}
      >
        {children}
      </MobileModal>
    );
  }

  return (
    <div
      ref={modalRef}
      className={classNames(
        'md:w-[var(--push-across-sidebar-current-width)]',
        'bg-white [border-left:1px_solid_#dbdbdb] bottom-0 absolute right-0 top-0 max-[767px]:h-screen',
        'max-[767px]:left-0 max-[767px]:fixed max-[767px]:right-0 max-[767px]:top-0',
        {
          hidden: !isOpen,
          block: isOpen,
          'md:border-0': isFabricGrid,
        }
      )}
      data-xc="PushAcrossSidebar"
    >
      <div
        className={classNames(
          'h-screen max-h-screen overflow-x-hidden relative overflow-y-auto md:overflow-y-auto md:sticky md:max-h-[calc(100vh_-_55px)] md:top-[55px] [&::-webkit-scrollbar]:hidden',
          {
            'md:right-0': isFabricGrid,
            PushAcrossSidebar__photo: !!isPhotoModalOpen,
            'md:z-30': isFabricGrid && !!isPhotoModalOpen,
          }
        )}
        ref={stickyRef}
        style={{
          top: windowWidth !== 768 ? `${desktopFilterSize + 55}px` : 0,
        }}
        data-xc="PushAcrossSidebar__sticky"
      >
        <div
          className={classNames(
            'w-[var(--push-across-sidebar-open-width)] max-[767px]:pb-[78px]',
            {
              'md:min-h-screen': isFullHeight && isOpen,
              'md:bg-white': isFabricGrid,
            }
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

PushAcrossSidebar.propTypes = {
  children: PropTypes.node.isRequired,
  desktopFilterSize: PropTypes.number,
  isFullHeight: PropTypes.bool,
  isMaterialSwatch: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  isPhotoModalOpen: PropTypes.bool,
  mobileClassName: PropTypes.string,
  mobileContentNoPadding: PropTypes.bool,
  mobileContentUnderHeader: PropTypes.bool,
  mobileHeaderRight: PropTypes.node,
  mobileHeaderRightClassName: PropTypes.string,
  mobileHideHeader: PropTypes.bool,
  mobileOnClose: PropTypes.func.isRequired,
  mobileTitle: PropTypes.node,
  mobileVariant: PropTypes.string,
  stickyRef: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['fabric-grid']),
};

export default withBrowserOnly(PushAcrossSidebar);
